import React, { useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Toast, ToastMessage } from "primereact/toast";
import { getOrderAddress } from "../../services/orders/orderservice";
import GetAddressByLocation from "../user-address/location-search/get-location";
import UserAddressService from "../../services/useraddress/useraddress.service";

interface OrderAddressInfoDTO {
  orderID: number;
  address1: string;
  address2: string;
  landmark: string;
  gpsLocation: string | null;
  userAddressID: number;
  name: string;
  primaryContactNbr: string;
  areaName: string;
  subDistrictName: string;
  cityName: string;
  stateName: string;
  countryName: string;
  zipcode: string;
}

const OrderSearch: React.FC = () => {
  const [orderId, setOrderId] = useState<string>("");
  const [orderInfo, setOrderInfo] = useState<OrderAddressInfoDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useRef<Toast>(null);
  const [isLocationMapVisible, setIsLocationMapVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };

  const getFormattedAddress = (info: OrderAddressInfoDTO) => {
    const parts = [
      info.address1,
      info.address2,
      info.landmark,
      info.areaName,
      info.subDistrictName,
      [info.cityName,info.stateName, info.countryName,info.zipcode ? `-${info.zipcode}` : null].filter(Boolean).join(" "),
    ];
    return parts.filter((part) => part && part.trim() !== "").join(", ");
  };

  const handleSearch = async () => {
  if (!orderId || orderId.trim() === "") {
    setOrderInfo(null); 
    showMessage("Info", "Please enter the Order ID to search.", "warn");
    return;
  }

  setLoading(true);
  setError(null);
  setOrderInfo(null);

  try {
    const data = await getOrderAddress(Number(orderId));
    if (data) {
      setOrderInfo(data);
    } else {
      setError("Order not found or server error.");
    }
  } catch (err: any) {
    setError("Unexpected error occurred.");
  } finally {
    setLoading(false);
  }
};

  const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const currLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            resolve(currLocation);
          },
          (error) => {
            console.error("Error getting current location:", error.message);
            reject(error);
          }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
        reject(new Error("Geolocation is not supported by this browser."));
      }
    });
  };

  const openDirections = (gpsLocation: string | null) => {
    if (!gpsLocation || !gpsLocation.includes(",")) {
      showMessage("Invalid GPS", "GPS location not available. Please update the GPS.", "error");
      return;
    }

    const gpsData = gpsLocation.split(",");
    if (gpsData.length !== 2 || isNaN(+gpsData[0]) || isNaN(+gpsData[1])) {
      showMessage("Invalid GPS", "GPS coordinates are invalid.", "error");
      return;
    }

    getCurrentLocation()
      .then((currentLocation) => {
        const start = `${currentLocation.latitude},${currentLocation.longitude}`;
        const destination = `${gpsData[0]},${gpsData[1]}`;
        const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
          start
        )}&destination=${encodeURIComponent(destination)}`;
        window.open(url, "_blank");
      })
      .catch((error) => {
        console.error("Error:", error.message);
        showMessage("Invalid GPS", "Please update the GPS", "error");
      });
  };

  const updateLatitudeAndLongitude = (location: string) => {
    if (location) {
      try {
        const [lat, lng] = location.split(",");
        setLatitude(parseFloat(lat));
        setLongitude(parseFloat(lng));
      } catch {}
    }
  };

  const updateGpsLocation = async (latLng: string) => {
    setIsLocationMapVisible(false);
    updateLatitudeAndLongitude(latLng);
    if (latLng && selectedId != null && selectedOrderId != null) {
      const response = await UserAddressService.updateGPSLocation1(selectedId, latLng, selectedOrderId);
      if (response && response.isSuccess) {
        showMessage("Success", "GPS location updated successfully.", "success");
        handleSearch();
      } else {
        showMessage("Error", response?.message || "Failed to update GPS location.", "error");
      }
    }
  };

  return (
    <div className="p-6 border border-gray-300 rounded-md shadow bg-white max-w-xl mx-auto mt-10">
      <Toast ref={toast} position="top-right" />
      <h3 className="text-xl font-semibold mb-4 text-gray-700">Search Order by ID</h3>

      <div className="flex items-center gap-4 mb-4">
        <InputText
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="Enter Order ID"
        />
        <Button label="Search" icon="pi pi-search" onClick={handleSearch} loading={loading} />
      </div>

      {error && <p className="text-red-600">{error}</p>}

      {orderInfo && (
        <Card title="Order Address Info" className="mt-4 bg-gray-50">
          <p><strong>Name:</strong> {orderInfo.name}</p>
          <p><strong>Primary Contact:</strong> {orderInfo.primaryContactNbr}</p>
          <p><strong>Address:</strong> {getFormattedAddress(orderInfo)}</p>
          <div className="mt-4">
            <p className="font-semibold text-gray-700 mb-2"><strong>GPS Location:</strong></p>
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
              <Button
                icon="pi pi-map-marker"
                label="Set Location"
                className="p-button-outlined p-button-info"
                onClick={() => {
                  setIsLocationMapVisible(true);
                  setSelectedId(orderInfo.userAddressID);
                  setSelectedOrderId(orderInfo.orderID);
                }}
                tooltip="Click to set or update GPS coordinates"
                tooltipOptions={{ position: "top" }}
              />
              <Button
                icon="pi pi-directions"
                label="Navigate"
                className="p-button-outlined p-button-success"
                onClick={() => openDirections(orderInfo?.gpsLocation)}
                tooltip="Open directions in Google Maps"
                tooltipOptions={{ position: "top" }}
              />
            </div>
          </div>
        </Card>
      )}

      <Dialog
        header="Search your location"
        className="location-dialog"
        visible={isLocationMapVisible}
        onHide={() => setIsLocationMapVisible(false)}
        footer={null}
        style={{ width: "50%" }}
      >
        <GetAddressByLocation
    getLatitudeLongitude={updateGpsLocation}
    lat={latitude}
    lng={longitude}
    formattedAddress={orderInfo ? getFormattedAddress(orderInfo) : ""}
  />
      </Dialog>
    </div>
  );
};

export default OrderSearch;
